// src/components/Header.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import { useNavigate, useLocation, useNavigationType } from "react-router-dom";
import { useUser, useAuth, SignOutButton, SignedIn } from "@clerk/clerk-react";
import Dropdown from "./Dropdown";
import {
  LogOut,
  MessageSquare,
  CreditCard,
  TrendingUp,
  X,
  Info,
} from "lucide-react";
import axios from "axios";
import { useProductStore } from "../store/useProductStore";
import NotificationsBell from "./notifications/NotificationsBell";
import AppPromoStrip from "./AppPromoStrip";

const OVERLAY_EVENT = "pixelads:overlay:open";
const ASSET_NOTICE_DISMISSED_KEY = "pixelads:asset-retention-notice-dismissed";
const ASSET_NOTICE_REFRESH_COUNT_KEY = "pixelads:asset-retention-notice-refresh-count";
const ASSET_NOTICE_RESHOW_EVERY = 5;

/** Resolve the backend base URL robustly */
const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window._API_BASE) ||
  "https://ecom-ads-ai-production.up.railway.app";

/** Rotating growth-style messages (PagePilot-style) */
const METRIC_MESSAGES = [
  {
    id: "products-analyzed",
    label: (
      <>
        <span className="text-white/70">Over </span>
        <span className="font-semibold text-[#FC2783]">8,400+ </span>
        <span className="text-white/70">products analyzed with </span>
        <span className="font-semibold text-white">PixelAds</span>
      </>
    ),
  },
  {
    id: "creatives-generated",
    label: (
      <>
        <span className="text-white/70">More than </span>
        <span className="font-semibold text-[#4ade80]">120,000+ </span>
        <span className="text-white/70">ad creatives shipped by </span>
        <span className="font-semibold text-white">PixelAds users</span>
      </>
    ),
  },
  {
    id: "hours-saved",
    label: (
      <>
        <span className="font-semibold text-[#FC2783]">19,000+ hrs </span>
        <span className="text-white/70">saved on manual design &amp; editing</span>
      </>
    ),
  },
  {
    id: "roas",
    label: (
      <>
        <span className="text-white/70">Average </span>
        <span className="font-semibold text-[#fbbf24]">+27% </span>
        <span className="text-white/70">lift in </span>
        <span className="font-semibold text-white">ROAS </span>
        <span className="text-white/70">after switching to PixelAds</span>
      </>
    ),
  },
];

export default function Header() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const { selectedProduct, setSelectedProduct } = useProductStore();

  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [metricIndex, setMetricIndex] = useState(0);
  const [isMetricFading, setIsMetricFading] = useState(false);
  const [promoHeight, setPromoHeight] = useState(0);
  const [showAssetRetentionNotice, setShowAssetRetentionNotice] = useState(false);

  useEffect(() => {
    try {
      const dismissed =
        sessionStorage.getItem(ASSET_NOTICE_DISMISSED_KEY) === "1";
  
      const currentCount = Number(
        sessionStorage.getItem(ASSET_NOTICE_REFRESH_COUNT_KEY) || "0"
      );
  
      const nextCount = currentCount + 1;
      sessionStorage.setItem(ASSET_NOTICE_REFRESH_COUNT_KEY, String(nextCount));
  
      if (!dismissed) {
        setShowAssetRetentionNotice(true);
        return;
      }
  
      if (nextCount >= ASSET_NOTICE_RESHOW_EVERY) {
        sessionStorage.removeItem(ASSET_NOTICE_DISMISSED_KEY);
        sessionStorage.setItem(ASSET_NOTICE_REFRESH_COUNT_KEY, "0");
        setShowAssetRetentionNotice(true);
        return;
      }
  
      setShowAssetRetentionNotice(false);
    } catch {
      setShowAssetRetentionNotice(true);
    }
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const userDropdownRef = useRef(null);

  // refs to avoid re-fetch loops & dependency churn
  const productsRef = useRef([]);
  const selectedRef = useRef(null);
  const matchProjectIdRef = useRef(null);
  const fetchInFlightRef = useRef(false);
  const lastFetchAtRef = useRef(0);

  // prevent repeated resync loops
  const lastUrlAppliedIdRef = useRef(null);

  // POP GUARD
  const historyPatchedRef = useRef(false);
  const originalPushStateRef = useRef(null);
  const originalReplaceStateRef = useRef(null);
  const popLockUntilRef = useRef(0);
  const popLockedIdRef = useRef(null);

  useEffect(() => {
    selectedRef.current = selectedProduct || null;
  }, [selectedProduct]);

  const getInitials = () => {
    if (!user) return "U";

    const first = user.firstName?.trim();
    const last = user.lastName?.trim();

    if (first && last) {
      return `${first[0]}${last[0]}`.toUpperCase();
    }

    const full =
      user.fullName ||
      user.username ||
      user.primaryEmailAddress?.emailAddress ||
      "";

    if (full) {
      const parts = full.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return parts[0].slice(0, 2).toUpperCase();
    }

    return "U";
  };

  const initials = getInitials();

  const matchProjectId =
    location.pathname.match(/\/project\/([^/]+)/)?.[1] ||
    location.pathname.match(/\/generate\/(?:[a-z-]+)\/([^/]+)/)?.[1] ||
    null;

  useEffect(() => {
    matchProjectIdRef.current = matchProjectId;
  }, [matchProjectId]);

  useEffect(() => {
    if (historyPatchedRef.current) return;
    if (typeof window === "undefined" || !window.history) return;

    historyPatchedRef.current = true;

    const extractProductIdFromUrl = (rawUrl) => {
      if (!rawUrl) return null;

      let path = "";
      try {
        if (typeof rawUrl === "string") {
          const u = rawUrl.startsWith("http")
            ? new URL(rawUrl)
            : new URL(rawUrl, window.location.origin);
          path = u.pathname || "";
        } else if (rawUrl instanceof URL) {
          path = rawUrl.pathname || "";
        } else {
          return null;
        }
      } catch {
        if (typeof rawUrl === "string") {
          path = rawUrl.split("?")[0].split("#")[0] || "";
        }
      }

      const m1 = path.match(/\/project\/([^/]+)/);
      if (m1?.[1]) return m1[1];

      const m2 = path.match(/\/generate\/(?:[a-z-]+)\/([^/]+)/);
      if (m2?.[1]) return m2[1];

      return null;
    };

    const shouldBlockDuringPopLock = (urlArg) => {
      const now = Date.now();
      if (now >= popLockUntilRef.current) return false;

      const lockedId = popLockedIdRef.current;
      if (!lockedId) return false;

      const targetId = extractProductIdFromUrl(urlArg);
      if (!targetId) return false;

      return targetId !== lockedId;
    };

    originalPushStateRef.current = window.history.pushState.bind(window.history);
    originalReplaceStateRef.current = window.history.replaceState.bind(window.history);

    window.history.pushState = function patchedPushState(state, title, url) {
      if (shouldBlockDuringPopLock(url)) return;
      return originalPushStateRef.current(state, title, url);
    };

    window.history.replaceState = function patchedReplaceState(state, title, url) {
      if (shouldBlockDuringPopLock(url)) return;
      return originalReplaceStateRef.current(state, title, url);
    };

    return () => {
      try {
        if (originalPushStateRef.current) {
          window.history.pushState = originalPushStateRef.current;
        }
        if (originalReplaceStateRef.current) {
          window.history.replaceState = originalReplaceStateRef.current;
        }
      } catch {
        // ignore
      }
    };
  }, []);

  useEffect(() => {
    if (navigationType !== "POP") return;

    popLockedIdRef.current = matchProjectId || null;
    popLockUntilRef.current = Date.now() + 700;

    const t = window.setTimeout(() => {
      if (Date.now() >= popLockUntilRef.current) {
        popLockedIdRef.current = null;
      }
    }, 750);

    return () => window.clearTimeout(t);
  }, [navigationType, matchProjectId]);

  useLayoutEffect(() => {
    if (!matchProjectId) return;

    if (lastUrlAppliedIdRef.current === matchProjectId) return;
    lastUrlAppliedIdRef.current = matchProjectId;

    try {
      localStorage.setItem("selectedProductId", matchProjectId);
    } catch {
      // ignore
    }

    const current = selectedRef.current;
    const currentId = current?.id;

    if (currentId === matchProjectId) return;

    const list = productsRef.current || [];
    const found = list.find((p) => p.id === matchProjectId);

    if (found) {
      selectedRef.current = found;
      setSelectedProduct(found);
      return;
    }

    const placeholder = {
      id: matchProjectId,
      product_name: "Loading...",
      url: "",
      title: "",
      name: "",
      thumbnail_url: "",
    };

    selectedRef.current = placeholder;
    setSelectedProduct(placeholder);
  }, [matchProjectId, setSelectedProduct]);

  const fetchProjects = useCallback(
    async ({ force = false } = {}) => {
      if (!user) return;

      const now = Date.now();
      const hadList =
        Array.isArray(productsRef.current) && productsRef.current.length > 0;

      if (!force && hadList && now - lastFetchAtRef.current < 15000) return;
      if (fetchInFlightRef.current) return;

      fetchInFlightRef.current = true;

      let token;
      try {
        token = await getToken();
        if (!token) return;
      } catch {
        return;
      }

      try {
        const projectsRes = await axios.get(`${API_BASE}/api/projects`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });

        const projs = Array.isArray(projectsRes.data) ? projectsRes.data : [];
        setProducts(projs);
        productsRef.current = projs;
        lastFetchAtRef.current = Date.now();

        const urlId = matchProjectIdRef.current;
        const storedId = localStorage.getItem("selectedProductId");
        const current = selectedRef.current;
        const currentId = current?.id;

        const isPlaceholder =
          !!current &&
          (!current.product_name || current.product_name === "Loading...") &&
          !current.url &&
          !current.thumbnail_url &&
          !current.title &&
          !current.name;

        if (urlId) {
          const foundUrl = projs.find((p) => p.id === urlId);

          if (foundUrl && (currentId !== foundUrl.id || isPlaceholder)) {
            selectedRef.current = foundUrl;
            setSelectedProduct(foundUrl);
            localStorage.setItem("selectedProductId", foundUrl.id);
          }
          return;
        }

        const foundStored = projs.find((p) => p.id === storedId);
        if (foundStored && currentId !== foundStored.id) {
          selectedRef.current = foundStored;
          setSelectedProduct(foundStored);
          return;
        }

        if (currentId && projs.find((p) => p.id === currentId)) return;

        if (projs.length > 0) {
          selectedRef.current = projs[0];
          setSelectedProduct(projs[0]);
          localStorage.setItem("selectedProductId", projs[0].id);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch header data:", err?.message || err);
      } finally {
        fetchInFlightRef.current = false;
      }
    },
    [getToken, user, setSelectedProduct]
  );

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    const onRefresh = () => fetchProjects({ force: true });
    window.addEventListener("pixelads:projects:refresh", onRefresh);
    return () => window.removeEventListener("pixelads:projects:refresh", onRefresh);
  }, [fetchProjects]);

  useEffect(() => {
    if (METRIC_MESSAGES.length <= 1) return;

    let timeoutId;
    const intervalId = window.setInterval(() => {
      setIsMetricFading(true);

      timeoutId = window.setTimeout(() => {
        setMetricIndex((prev) => (prev + 1) % METRIC_MESSAGES.length);
        setIsMetricFading(false);
      }, 200);
    }, 4000);

    return () => {
      window.clearInterval(intervalId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onOverlayOpen = (e) => {
      const src = e?.detail?.source;
      if (src && src !== "avatar") setUserDropdownOpen(false);
    };
    window.addEventListener(OVERLAY_EVENT, onOverlayOpen);
    return () => window.removeEventListener(OVERLAY_EVENT, onOverlayOpen);
  }, []);

  const onAvatarToggle = useCallback(() => {
    setUserDropdownOpen((prev) => {
      const next = !prev;
      if (next) {
        try {
          window.dispatchEvent(
            new CustomEvent(OVERLAY_EVENT, { detail: { source: "avatar" } })
          );
        } catch {
          // ignore
        }
      }
      return next;
    });
  }, []);

  const handleDismissAssetRetentionNotice = useCallback(() => {
    try {
      sessionStorage.setItem(ASSET_NOTICE_DISMISSED_KEY, "1");
      sessionStorage.setItem(ASSET_NOTICE_REFRESH_COUNT_KEY, "0");
    } catch {
      // ignore
    }
    setShowAssetRetentionNotice(false);
  }, []);

  const handleProductSelect = (item) => {
    if (!item?.id || item.id === selectedRef.current?.id) return;

    selectedRef.current = item;
    setSelectedProduct(item);
    localStorage.setItem("selectedProductId", item.id);

    const projectIdRegex = /(\/project\/|\/generate\/[a-z-]+\/)([^/]+)/i;
    if (projectIdRegex.test(location.pathname)) {
      const newPath = location.pathname.replace(
        projectIdRegex,
        (_, prefix) => `${prefix}${item.id}`
      );
      navigate(newPath + location.search + location.hash, {
        replace: false,
        state: { source: "productDropdown" },
      });
    } else {
      navigate(`/project/${item.id}`, {
        replace: false,
        state: { source: "productDropdown" },
      });
    }
  };

  const handleLiveChat = () => {
    if (
      typeof window !== "undefined" &&
      window.Tawk_API &&
      typeof window.Tawk_API.maximize === "function"
    ) {
      window.Tawk_API.maximize();
    } else {
      // eslint-disable-next-line no-console
      console.log("Live chat clicked (Tawk.to not ready yet)");
    }
  };

  const hideHeader =
    location.pathname.startsWith("/pricing") || location.pathname.startsWith("/admin");

  if (hideHeader) return null;

  const primaryEmail =
    user?.primaryEmailAddress?.emailAddress ||
    user?.emailAddresses?.[0]?.emailAddress ||
    "";

  const displayName = user?.fullName || primaryEmail || "Account";

  return (
    <SignedIn>
      <AppPromoStrip onHeightChange={setPromoHeight} />

      <header
        className="fixed left-0 right-0 z-40 w-full bg-[#15152B] border-b border-[#2A2A40] min-h-[64px] md:min-h-[72px]"
        style={{ top: promoHeight }}
      >
        <button
          onClick={() => navigate("/generate")}
          className="hidden md:flex items-center absolute left-3 top-1/2 -translate-y-1/2"
          aria-label="Go to Generate"
          style={{ cursor: "pointer" }}
        >
          <img
            src={`${process.env.PUBLIC_URL || ""}/media/logo.png`}
            alt="ShopAds.ai"
            className="w-24 md:w-28 lg:w-32 xl:w-36 h-auto select-none"
            draggable={false}
          />
        </button>

        <div className="pl-0 md:pl-[260px]">
          <div className="w-full px-3 sm:px-8">
            <div className="mx-auto max-w-[1600px] py-2 md:py-0">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,2fr)_auto] items-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                  <button
                    onClick={() => navigate("/generate")}
                    className="flex md:hidden items-center flex-shrink-0"
                    aria-label="Go to Generate"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL || ""}/media/logo-icon.png`}
                      onError={(e) => {
                        e.currentTarget.src = `${process.env.PUBLIC_URL || ""}/media/logo.png`;
                      }}
                      alt="ShopAds.ai"
                      className="w-10 sm:w-24 h-auto select-none"
                      draggable={false}
                    />
                  </button>

                  <div className="w-full flex-1 min-w-0 max-w-none sm:max-w-[36rem]">
                    <Dropdown
                      label="Select Product"
                      options={products}
                      selected={selectedProduct}
                      onSelect={handleProductSelect}
                    />
                  </div>

                  <div className="hidden md:flex items-center flex-shrink-0">
                    <div className="rounded-2xl bg-[#111827] border border-[#2563EB]/40 px-4 py-1.5 text-white">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#1D2A3F] text-[#60A5FA]">
                          <TrendingUp size={16} strokeWidth={2.2} />
                        </span>
                        <span
                          key={METRIC_MESSAGES[metricIndex].id}
                          className={`text-sm sm:text-[0.95rem] font-medium transition-all duration-200 transform ${
                            isMetricFading
                              ? "opacity-0 -translate-y-1"
                              : "opacity-100 translate-y-0"
                          }`}
                        >
                          {METRIC_MESSAGES[metricIndex].label}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end">
                  <div
                    className="relative flex items-center gap-2 sm:gap-4"
                    ref={userDropdownRef}
                  >
                    <button
                      onClick={onAvatarToggle}
                      className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg bg-gradient-to-br from-[#FC2783] to-[#9C27B0] flex items-center justify-center text-white font-bold shadow select-none text-xs sm:text-sm"
                      aria-label="User menu"
                      aria-expanded={userDropdownOpen}
                    >
                      {initials}
                    </button>

                    <NotificationsBell />

                    <div
                      className={`absolute right-0 top-full mt-3 w-[min(16rem,calc(100vw-1.5rem))] bg-[#181828] border border-[#3a3a4d] rounded-2xl shadow-2xl shadow-black/40 backdrop-blur-sm z-50 text-white text-sm font-semibold select-none overflow-hidden transform origin-top-right transition-all duration-150 ${
                        userDropdownOpen
                          ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
                          : "opacity-0 scale-95 -translate-y-1 pointer-events-none"
                      }`}
                    >
                      <div className="px-4 pt-3 pb-3 flex items-center gap-3 bg-gradient-to-r from-[#1f1f3a] to-[#181828] border-b border-white/5">
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#FC2783] to-[#9C27B0] flex items-center justify-center text-xs font-bold">
                          {initials}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{displayName}</div>
                          {primaryEmail ? (
                            <div className="text-[11px] text-white/60 truncate">
                              {primaryEmail}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="py-1">
                        <button
                          type="button"
                          onClick={handleLiveChat}
                          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#2d2d45] transition-colors"
                        >
                          <span className="flex items-center gap-2">
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#1f2937] text-[#f97316]">
                              <MessageSquare size={16} />
                            </span>
                            <span>Live chat</span>
                          </span>
                        </button>

                        <button
                          type="button"
                          onClick={() => navigate("/pricing")}
                          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#2d2d45] transition-colors"
                        >
                          <span className="flex items-center gap-2">
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#1f2937] text-[#22c55e]">
                              <CreditCard size={16} />
                            </span>
                            <span>Billing</span>
                          </span>
                        </button>

                        <SignOutButton>
                          <button
                            type="button"
                            className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#2d2d45] transition-colors"
                          >
                            <span className="flex items-center gap-2">
                              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#1f2937] text-[#f97373]">
                                <LogOut size={16} />
                              </span>
                              <span>Log out</span>
                            </span>
                          </button>
                        </SignOutButton>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </header>

        {showAssetRetentionNotice ? (
  <div
    className="fixed right-4 sm:right-6 z-[80] w-[calc(100vw-2rem)] max-w-[430px]"
    style={{ top: `calc(${promoHeight}px + 84px)` }}
  >
    <div className="rounded-2xl border border-yellow-500/60 bg-[#171722]/95 backdrop-blur-md shadow-2xl shadow-black/40">
      <div className="flex items-start gap-3 px-4 py-3">
        <span className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-yellow-500/15 text-yellow-300 border border-yellow-500/30">
          <Info size={15} />
        </span>

        <div className="min-w-0 flex-1 pr-1">
          <div className="text-sm font-semibold text-yellow-200">
            Asset retention notice
          </div>
          <div className="mt-1 text-sm leading-6 text-white/75">
            Generated assets are automatically deleted 30 days after creation.
          </div>
        </div>

        <button
          type="button"
          onClick={handleDismissAssetRetentionNotice}
          className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-white/50 transition hover:bg-white/5 hover:text-white"
          aria-label="Dismiss asset retention notice"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  </div>
) : null}
</SignedIn>
  );
}